import { useTranslation } from 'react-i18next';
import TikTokGlyph from 'src/assets/tiktok-glyph.svg';
import { eFormatType } from 'src/pages/WhatsappApi/WhatsappApi.enum';
import { MediaType } from '../../../../../../../../Chat.enum';
import { tChatEvent } from '../../../../../../../../Chat.types';
import { resolveQuotedCard } from './quotedCarousel.functions';
import { isLottieSticker } from '../../../../../../../../lottieSticker.functions';

// Vite svgr exports default as React component at runtime, even though typed
// as string. Cast mirrors AutomationSelection.component.tsx.
const TikTokGlyphSvg = TikTokGlyph as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

export type tQuotedMessageProps = tChatEvent & {
  /**
   * Which carousel card the REPLYING message tapped (INT-1560) — read off the
   * reply, not off this quoted event, which is the template that offered the
   * cards. Undefined for every other quote, and for a tap on a carousel
   * delivered before INT-1560: absence means "unknown card", so it falls back
   * to the plain template quote rather than to card 0.
   */
  carouselCardIndex?: number | null;
};

export function QuotedMessage(props: tQuotedMessageProps) {
  const { t } = useTranslation('chat/chat');
  const { content, carouselCardIndex } = props;
  const attachments = content?.attachments || [];
  const text = content?.text || '';

  // INT-1560: when the reply names a card, quote THAT card — its own copy and
  // its media — instead of the template body every card shares. Without this
  // the operator reads the same quote whichever card the lead chose, which is
  // the bug: three cards, one button copy, one indistinguishable reply.
  const quotedCard = resolveQuotedCard(content, carouselCardIndex);

  if (quotedCard) {
    return (
      <div
        className="flex items-center gap-2"
        data-testid="quoted-carousel-card"
      >
        {quotedCard.mediaUrl !== null &&
          (quotedCard.mediaFormat === eFormatType.VIDEO ? (
            <video
              src={quotedCard.mediaUrl}
              className="w-10 h-10 object-cover rounded shrink-0"
              data-testid="quoted-carousel-card-video"
            />
          ) : (
            <img
              src={quotedCard.mediaUrl}
              alt=""
              className="w-10 h-10 object-cover rounded shrink-0"
              loading="lazy"
              data-testid="quoted-carousel-card-image"
            />
          ))}

        {quotedCard.text !== null && (
          <span className="line-clamp-2">{quotedCard.text}</span>
        )}
      </div>
    );
  }

  return (
    <div>
      {text !== '' && <span>{text}</span>}

      <div className="my-2">
        {attachments.map((attachment) => {
          const mediaType = attachment.mediaType;

          // INT-1708: before the switch, because a Lottie sticker quoted from
          // the socket is an IMAGE and from the history a STICKER; either way
          // an <img> cannot show it.
          if (isLottieSticker(attachment)) {
            return <span>{t('stickerLabel')}</span>;
          }

          switch (mediaType) {
            case MediaType.IMAGE:
              return (
                <img
                  src={attachment.url ?? ''}
                  alt="Imagen"
                  className="w-full max-h-72 object-cover"
                  loading="lazy"
                />
              );
            case MediaType.STICKER:
              return (
                <img
                  src={attachment.url ?? ''}
                  alt="Imagen"
                  className="w-full max-h-32 object-cover"
                  loading="lazy"
                />
              );
            case MediaType.VIDEO:
              return (
                <video
                  src={attachment.url ?? ''}
                  className="w-full max-h-52 object-cover"
                  controls
                />
              );
            case MediaType.DOCUMENT:
              return <span>Archivo</span>;
            case MediaType.AUDIO:
              return <audio src={attachment.url ?? ''} controls />;
            // INT-929: TikTok shared video posts. The default branch's
            // "Archivo" label would be misleading — show the TikTok-glyph
            // + localized label instead. Quoted-reply UI stays compact:
            // no thumbnail.
            case MediaType.SHARE_POST:
              return (
                <span
                  className="inline-flex items-center gap-1"
                  data-testid="quoted-share-post"
                >
                  <TikTokGlyphSvg className="w-4 h-4" />
                  {t('sharedTikTokVideo')}
                </span>
              );
            default:
              return <span>Archivo</span>;
          }
        })}
      </div>
    </div>
  );
}
